import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";


const CollateralNotePreview = ({ data }) => {
  const [note, setNote] = useState({});
  const [signatures, setSignatures] = useState({}); // only need client image
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;
  const { patientFormId, formTitle, practiceLogo, particeName } = data || {};

  // Format date as MM-DD-YYYY
  const fmtDate = useCallback((val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }, []);

  // Format date-time as MM/DD/YYYY hh:mm AM/PM
  const fmtDateTime = useCallback((val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    let h = d.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    const hh = String(h).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
  }, []);

  const firstNonEmpty = (...vals) =>
    vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") ||
    "";

  // Strip "Digitally signed by ..." and trailing "on <date/time>"
  const stripDigitalArtifacts = (s) => {
    if (s == null) return "";
    let t = String(s).trim();
    t = t.replace(/^digitally\s*signed\s*by\s*/i, "");
    t = t.replace(
      /\s*on\s+\d{1,2}[/-]\d{1,2}[/-]\d{2,4}(?:\s+\d{1,2}:\d{2}\s*(?:AM|PM))?\.?$/i,
      ""
    );
    return t.replace(/\.$/, "").trim();
  };

  // Try to pull a date out of a phrase text
  const extractDateFromDigitalText = (s) => {
    if (!s) return "";
    const str = String(s);
    const m = str.match(
      /on\s+(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}(?:\s+\d{1,2}:\d{2}\s*(AM|PM))?)/i
    );
    if (m && m[1]) return m[1].trim().replace(/\.$/, "");
    const m2 = str.match(
      /(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}(?:\s+\d{1,2}:\d{2}\s*(AM|PM))?)/i
    );
    if (m2 && m2[1]) return m2[1].trim().replace(/\.$/, "");
    return "";
  };

  useEffect(() => {
    let active = true;
    const revokeList = [];

    const getPreviewLink = async (path, token) => {
      try {
        if (!path) return "";
        // If direct URL (http/data), use it as-is
        if (/^(https?:|data:)/i.test(path)) return path;

        const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
          path
        )}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return "";

        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("text/plain")) {
          const link = await res.text();
          return link || "";
        }

        const blob = await res.blob();
        if (blob && blob.size > 0) {
          const objUrl = URL.createObjectURL(blob);
          revokeList.push(objUrl);
          return objUrl;
        }
        return "";
      } catch {
        return "";
      }
    };

    const fetchSignatures = async (noteData, token) => {
      // Only need client signature image link (like Crisis Plan)
      const clientPath = firstNonEmpty(
        noteData.clientsURL,
        noteData.clientSignatureURL,
        noteData.patientSignatureURL,
        noteData.imageURLClientSignature
      );
      const loaded = {};
      if (clientPath) {
        loaded.client = await getPreviewLink(clientPath, token);
      }
      if (active) setSignatures(loaded);
    };

    const fetchNote = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        if (!token) throw new Error("Authentication token not found.");
        if (!API_BASE_URL) throw new Error("API base URL is not configured.");

        const [formRes, noteRes] = await Promise.all([
          fetch(`${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(
            `${API_BASE_URL}/CollateralNote/getCollateralNoteByFormId?patientFormId=${patientFormId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        if (formRes.status === 204 || noteRes.status === 204) {
          if (active) {
            setNote({});
            setSignatures({});
          }
          return;
        }

        if (!formRes.ok || !noteRes.ok) throw new Error("Failed to load data.");

        const [formData, noteData] = await Promise.all([
          formRes.json(),
          noteRes.json(),
        ]);

        const merged = { ...noteData, ...formData };
        if (active) {
          setNote(merged);
          await fetchSignatures(merged, token);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (patientFormId) fetchNote();

    return () => {
      active = false;
      try {
        revokeList.forEach((u) => u.startsWith("blob:") && URL.revokeObjectURL(u));
      } catch { }
    };
  }, [patientFormId, API_BASE_URL]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "60vh",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // Helper: format date for "Signed on MM-DD-YYYY"
  const sigDate = (v) => {
    const d = fmtDate(v);
    return d === "—" ? "" : d;
  };

  // Client
  const clientName = stripDigitalArtifacts(
    firstNonEmpty(note.clientSignatureName, note.patientSignatureName)
  );
  let clientDate =
    firstNonEmpty(note.patientSignatureDATE, note.clientSignatureDate) ||
    extractDateFromDigitalText(
      firstNonEmpty(
        note.clientSignatureName,
        note.patientSignatureName,
        note.clientsURL,
        note.clientSignatureURL,
        note.patientSignatureURL
      )
    );

  // Rehab Worker
  const rehabName = stripDigitalArtifacts(
    firstNonEmpty(
      note.RehabWorkermassagename,
      note.rehabWorkerName,
      note.rehabWorker
    )
  );
  let rehabDate = firstNonEmpty(
    note.rehabWorkerSignatureDATE,
    note.rehabWorkerSignatureDate,
    note.rehabWorkerSignDate,
    note.rehabWorkerDate
  );
  if (!rehabDate) {
    rehabDate = extractDateFromDigitalText(
      firstNonEmpty(note.rehabWorker, note.RehabWorkermassagename)
    );
  }

  // Practitioner
  const practitionerName = stripDigitalArtifacts(
    firstNonEmpty(
      note.HealthPractitionermassagename,
      note.healthPractitionerName,
      note.healthPractice
    )
  );
  let practitionerDate = firstNonEmpty(
    note.healthPractitionerSignatureDATE,
    note.healthPractitionerSignatureDate,
    note.healthPractitionerDate
  );
  if (!practitionerDate) {
    practitionerDate = extractDateFromDigitalText(
      firstNonEmpty(note.healthPractice, note.HealthPractitionermassagename)
    );
  }

  // Staff
  const staffName = stripDigitalArtifacts(
    firstNonEmpty(
      note.massagestaffSignature,
      note.staffSignatureName,
      note.staffSignature
    )
  );
  let staffDate = firstNonEmpty(
    note.staffsignatureDATE,
    note.staffSignatureDATE,
    note.staffSignDate,
    note.staffSignatureDate
  );
  if (!staffDate) {
    staffDate = extractDateFromDigitalText(
      firstNonEmpty(note.staffSignature, note.massagestaffSignature)
    );
  }

  // Supervisor
  const supervisorName = stripDigitalArtifacts(
    firstNonEmpty(
      note.massageSupervisorSignature,
      note.supervisorSignatureName,
      note.supervisorSignature
    )
  );
  let supervisorDate = firstNonEmpty(
    note.supervisorSignatureDATE,
    note.SupervisorDate,
    note.supervisorDate
  );
  if (!supervisorDate) {
    supervisorDate = extractDateFromDigitalText(
      firstNonEmpty(note.supervisorSignature, note.massageSupervisorSignature)
    );
  }

  // Director
  const directorName = stripDigitalArtifacts(
    firstNonEmpty(
      note.messagetreatmentdirector,
      note.treatmentdirectormassagename,
      note.treatmentDirectorName,
      note.directorSignatureName
    )
  );
  const directorUser = stripDigitalArtifacts(
    firstNonEmpty(
      note.TreatmentDirectorid,
      note.treatmentDirectorId,
      note.treatmentdirectorId,
      note.directorUserName,
      note.directorUsername
    )
  );
  // Phrase date-time (for "Digitally signed by ... on MM/DD/YYYY hh:mm AM/PM.")
  let directorPhraseDT = firstNonEmpty(
    note.treatmentDirectorSignatureDATE,
    note.treatmentDirectorSignatureDate
  );
  if (!directorPhraseDT) {
    directorPhraseDT = extractDateFromDigitalText(
      firstNonEmpty(
        note.messagetreatmentdirector,
        note.treatmentdirectormassagename,
        note.treatmentDirectorName,
        note.directorSignatureName
      )
    );
  }
  // Plain date (for "Signed on MM-DD-YYYY")
  let directorDate = firstNonEmpty(
    note.treatmentdirectorDate,
    note.directorDate
  );

  // Generic signature row: image (client only) else name + date (like Crisis Plan)
  const renderSignature = (label, imgLink, typedName, dateVal) => {
    const dateText = sigDate(dateVal);
    const hasAny = !!firstNonEmpty(imgLink, typedName, dateText);
    return (
      <tr>
        <td style={{ padding: "8px", width: "25%", verticalAlign: "top" }}>
          <Typography>{label}</Typography>
        </td>
        <td style={{ padding: "8px", width: "50%", verticalAlign: "top" }}>
          {imgLink ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src={imgLink}
                alt={`${label} Image`}
                style={{
                  display: "block",
                  width: "200px",
                  height: "80px",
                  border: "2px solid #000",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                  objectFit: "contain",
                }}
              />
              {dateText ? (
                <p style={{ fontWeight: 700, fontSize: "16px" }}>
                  Signed on {dateText}
                </p>
              ) : null}
            </div>
          ) : hasAny ? (
            <div>
              <Typography sx={{ fontWeight: "bold" }}>
                {typedName || "Digitally Signed"}
              </Typography>
              {dateText ? (
                <Typography variant="body2" color="text.secondary">
                  Signed on {dateText}
                </Typography>
              ) : null}
            </div>
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </td>
        <td style={{ padding: "8px", width: "25%", verticalAlign: "top" }} />
      </tr>
    );
  };

  // Director row: show both the digital phrase (with time) and the "Signed on MM-DD-YYYY"
  const digitalPhrase = (name, username, dateTimeVal) => {
    const n = stripDigitalArtifacts(name) || "—";
    const u = stripDigitalArtifacts(username);
    const dt = fmtDateTime(dateTimeVal);
    const who = u ? `${n}, ${u}` : n;
    return dt !== "—"
      ? `Digitally signed by ${who} on ${dt}.`
      : `Digitally signed by ${who}.`;
  };

  const renderDirectorRow = (
    label,
    name,
    username,
    phraseDT,
    signedDateVal
  ) => {
    const hasAny =
      !!firstNonEmpty(name, username, phraseDT, signedDateVal);
    const signedOn = sigDate(signedDateVal);

    return (
      <tr>
        <td style={{ padding: "8px", width: "25%", verticalAlign: "top" }}>
          <Typography>{label}</Typography>
        </td>
        <td style={{ padding: "8px", width: "50%", verticalAlign: "top" }}>
          {hasAny ? (
            <div>
              <Typography sx={{ fontWeight: 600 }}>
                {digitalPhrase(name, username, phraseDT)}
              </Typography>
              {signedOn ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Signed on {signedOn}
                </Typography>
              ) : null}
            </div>
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </td>
        <td style={{ padding: "8px", width: "25%", verticalAlign: "top" }} />
      </tr>
    );
  };

  return (
    <Paper
      sx={{
        p: 0,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
        pointerEvents: "none",
        "& *": { cursor: "not-allowed !important" },
      }}
    >
      {/* UPDATED: make header sticky and move it slightly up */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px", // pull header up so nothing shows above it (matches modal content padding p:2 = 16px)
          zIndex: (theme) => theme.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ position: "absolute", top: 16, left: 16 }}
        >
          <strong>DOS:</strong> {fmtDate(note.assessmentDate)}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          {formTitle || note.customFormTitle || "Collateral Note"}
        </Typography>

        {practiceLogo && (
          <Box
            component="img"
            src={practiceLogo}
            alt="Practice Logo"
            sx={{ width: 65, height: 65, borderRadius: 1, mb: 2, mx: "auto" }}
          />
        )}
        <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
          {note.particeName || particeName || ""}
        </Typography>
        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      <Box sx={{ px: 4 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {note.fileno || note.accountNo || "—"}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client:</strong>{" "}
              {note.patientName || note.PatientNameclient || "—"}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {fmtDate(note.dob)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={0}>
          <Grid item xs={6}>
            <TextField
              fullWidth={false}
              size="small"
              label="Title"
              value={note.customFormTitle || formTitle || "—"}
              InputProps={{ readOnly: true, disabled: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
          <Divider sx={{ my: 2, mx: 5 }} />
          <Grid item xs={6}>
            <TextField
              fullWidth={false}
              size="small"
              label="Session Date"
              value={fmtDate(note.sessionDate || note.assessmentDate)}
              InputProps={{ readOnly: true, disabled: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={note.printedAndFiled || false} disabled />}
              label="Printed and Filed"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={note.Isinactive || false} disabled />}
              label="Inactive"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={note.isAuditReady || false} disabled />}
              label="Audit Ready"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={note.islocked || false} disabled />}
              label="Lock"
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: "bold" }}>
          Collateral Note
        </Typography>
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>Client Name:</strong>
            </Typography>
          </Grid>
          <Divider sx={{ my: 2, mx: 4 }} />
          <Grid item xs={3}>
            <Typography variant="body2">
              {note.patientName || note.PatientNameclient || "—"}
            </Typography>
          </Grid>
          <Divider sx={{ my: 2, mx: 24 }} />
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="DOB"
              value={fmtDate(note.dob)}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{ "& .MuiSvgIcon-root": { display: "none" } }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={3}>
            <Typography variant="body2">
              <strong>File No:</strong>
            </Typography>
          </Grid>
          <Divider sx={{ my: 2, mx: 4 }} />
          <Grid item xs={3}>
            <Typography variant="body2">
              {note.fileno || note.accountNo || "—"}
            </Typography>
          </Grid>
          <Divider sx={{ my: 2, mx: 29 }} />
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Length of Session (mins)"
              value={note.lengthofSession || "—"}
              InputProps={{ readOnly: true, disabled: true }}
              sx={{ "& .MuiSvgIcon-root": { display: "none" } }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ minWidth: 100 }}>
                Contact Type:
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={note.collateralType || false} disabled />}
                label="Collateral"
              />
              <FormControlLabel
                control={<Checkbox checked={note.otherType || false} disabled />}
                label="Others"
              />
            </Box>
          </Grid>
          <Divider sx={{ my: 2, mx: 18.4 }} />
          <Grid item xs={6}>
            <TextField
              fullWidth={false}
              size="small"
              label="Other Detail"
              value={note.otherdetail || "—"}
              InputProps={{ readOnly: true, disabled: true }}
              InputLabelProps={{ shrink: true }}
              multiline
              minRows={1}
              sx={{
                width: "200px",
                "& .MuiOutlinedInput-root": { padding: 0 },
                "& .MuiInputBase-inputMultiline, & .MuiOutlinedInput-inputMultiline": {
                  padding: "6px 10px !important",
                },
                "& textarea": {
                  overflow: "hidden",
                  resize: "none",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  textIndent: 0,
                  margin: 0,
                },
                "& .MuiSvgIcon-root": { display: "none" },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: 4, mb: 2 }}>
        <TextField
          fullWidth
          label="Description"
          value={note.description || "—"}
          InputProps={{ readOnly: true, disabled: true }}
          multiline
          minRows={2}
          sx={{
            minHeight: 100,
            width: "832px",
            "& .MuiSvgIcon-root": { display: "none" },
          }}
        />
      </Box>

      <Divider sx={{ my: 3, mx: 4 }} />
      <Box sx={{ px: 4, pb: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Signatures
        </Typography>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {/* Client Signature: image if available, else typed name/date */}
            {renderSignature(
              "Client Signature:",
              signatures.client || note.imageURLClientSignature || "",
              clientName,
              clientDate
            )}

            {/* Rehab Worker */}
            {renderSignature(
              "Mental Health Rehab Worker:",
              "",
              rehabName,
              rehabDate
            )}

            {/* Practitioner */}
            {renderSignature(
              "Mental Health Practitioner:",
              "",
              practitionerName,
              practitionerDate
            )}

            {/* Staff */}
            {renderSignature("Staff Signature:", "", staffName, staffDate)}

            {/* Supervisor */}
            {renderSignature(
              "Supervisor Signature:",
              "",
              supervisorName,
              supervisorDate
            )}

            {/* ARMHS Treatment Director: show phrase + "Signed on" */}
            {renderDirectorRow(
              "ARMHS Treatment Director:",
              directorName,
              directorUser,
              directorPhraseDT,
              directorDate
            )}
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default CollateralNotePreview;
